import { useSearchParams } from 'react-router-dom';
import { cajaService } from '../../services/api';
import { money, segmento, segmentos } from '../../components/ui/estilos';
import MejoresDias from './MejoresDias';
import { ReporteHoras, ReporteMesas } from '../ReporteClientes';
import { delta, usePeriodo } from './periodo';
import { Barras, Encabezado, Kpis, Progreso, Tabla, Tarjeta } from './ui';
import { num, useReporte } from './useReporte';

const fila: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: 15 };

// Métricas derivadas de GET /caja/reporte-clientes.
function metricas(d: any) {
  if (!d) return null;
  const r = d.resumen || {};
  const mesas = (d.por_mesa || []).filter((m: any) => m.mesa != null);
  const cuentasMesa = mesas.reduce((s: number, m: any) => s + m.cuentas, 0);
  const minutos = cuentasMesa ? mesas.reduce((s: number, m: any) => s + num(m.minutos) * m.cuentas, 0) / cuentasMesa : 0;
  // Rotación: cuántas cuentas atendió cada mesa por día trabajado.
  const rotacion = mesas.length && r.dias_operados ? cuentasMesa / mesas.length / r.dias_operados : 0;
  // Conteo exacto del servidor (cruce mesa/día/hora con reservaciones); si no viene, resta simple.
  const w = d.walkins;
  const reservas = w ? w.cuentas_reserva : d.reservas?.completadas || 0;
  return {
    clientes: r.clientes || 0, porPersona: r.clientes ? num(r.total) / r.clientes : 0, minutos, rotacion,
    cuentas: r.cuentas || 0, cuentasMesa, reservas, walkIns: w ? w.cuentas : Math.max(0, cuentasMesa - reservas),
    noShow: d.reservas?.no_asistio || 0, reservasCanceladas: d.reservas?.canceladas || 0, cancelados: d.cancelados || { cuentas: 0, monto: 0, recientes: [] },
  };
}

export default function Clientes() {
  const p = usePeriodo('semana');
  const { datos, error } = useReporte(cajaService.getReporteClientes, p.actual, p.previo);
  const m = metricas(datos?.actual), mp = metricas(datos?.previo);
  const zonas: any[] = datos?.actual?.por_zona || [];
  const totalZonas = zonas.reduce((s, z) => s + z.clientes, 0);
  // "Mejores días" vive aquí como pestaña, con el mismo periodo (?t=dias).
  const [params, setParams] = useSearchParams();
  const tab = params.get('t') === 'dias' ? 'dias' : 'clientes';
  const dias = useReporte(cajaService.getVentasPorDia, p.actual, p.previo);
  const conReserva = m && m.cuentasMesa ? Math.round(m.reservas / Math.max(m.cuentasMesa, m.reservas) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Encabezado titulo="Clientes y operación" p={p} />
      <div role="tablist" aria-label="Sección" style={{ ...segmentos, width: 'fit-content', maxWidth: '100%' }}>
        {([['clientes', 'Clientes y operación'], ['dias', 'Mejores días']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === 'clientes' ? {} : { t: k }, { replace: true })}
            style={{ ...segmento(tab === k), height: 40, fontSize: 15, whiteSpace: 'nowrap' }}>{l}</button>
        ))}
      </div>
      {tab === 'dias' && <>
        {dias.error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{dias.error}</div>}
        {!dias.datos && !dias.error && <div style={{ color: 'var(--muted)' }}>Cargando…</div>}
        {dias.datos && <MejoresDias datos={dias.datos.actual} />}
      </>}
      {tab === 'clientes' && error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {tab === 'clientes' && !datos && !error && <div style={{ color: 'var(--muted)' }}>Cargando…</div>}
      {tab === 'clientes' && m && datos && <>
        <Kpis items={[
          { label: 'Clientes atendidos', valor: String(m.clientes), delta: delta(m.clientes, mp?.clientes || 0, p.periodo) },
          { label: 'Ticket prom. / persona', valor: money(m.porPersona), delta: delta(m.porPersona, mp?.porPersona || 0, p.periodo) },
          { label: 'Tiempo prom. de mesa', valor: `${Math.round(m.minutos)} min`, delta: delta(m.minutos, mp?.minutos || 0, p.periodo, true) },
          // Walk-ins al frente: normalmente son la mayoría de las mesas.
          { label: 'Llegaron sin reservación', valor: String(m.walkIns), delta: m.cuentasMesa ? { texto: `${100 - conReserva}% de las mesas`, color: 'var(--muted)' } : delta(m.walkIns, mp?.walkIns || 0, p.periodo) },
          { label: 'Rotación de mesas', valor: `${m.rotacion.toFixed(1)}x`, delta: { texto: 'cuentas por mesa al día', color: 'var(--muted)' } },
          { label: 'Cancelaciones', valor: String(m.cancelados.cuentas), delta: { texto: `${m.cuentas ? Math.round(m.cancelados.cuentas / (m.cuentas + m.cancelados.cuentas) * 100) : 0}% de tickets`, color: 'var(--muted)' } },
        ]} />
        {datos?.actual?.resumen?.sin_personas > 0 && (
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>{datos.actual.resumen.sin_personas} cuentas de mesa se cobraron sin número de personas; cuentan como 1 cliente.</div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 18, alignItems: 'stretch' }}>
          <Tarjeta titulo="Clientes por zona">
            {!zonas.length && <div style={{ color: 'var(--muted)' }}>Sin cuentas en este periodo.</div>}
            {zonas.map(z => <Progreso key={z.zona} label={z.zona} valor={`${Math.round(z.clientes / (totalZonas || 1) * 100)}%`} pct={z.clientes / (totalZonas || 1) * 100} />)}
          </Tarjeta>
          <Tarjeta titulo="Cómo llegan tus clientes">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 34, fontWeight: 700 }}>{m.cuentasMesa ? `${100 - conReserva}%` : '—'}</span>
              <span style={{ fontSize: 15, color: 'var(--muted)' }}>de las mesas llegan sin reservación</span>
            </div>
            <div role="img" aria-label={`Sin reservación ${100 - conReserva}%, con reservación ${conReserva}%`} style={{ height: 20, borderRadius: 999, overflow: 'hidden', display: 'flex', background: 'var(--well)' }}>
              <div style={{ height: '100%', background: 'var(--brand-texto)', width: `${100 - conReserva}%` }} />
            </div>
            <div style={{ ...fila, fontSize: 14, color: 'var(--muted)' }}>
              <span><span style={{ color: 'var(--brand-texto)' }}>●</span> Sin reservación · <b style={{ color: 'var(--ink)' }}>{m.walkIns}</b> mesas</span>
              <span>● Con reservación · <b style={{ color: 'var(--ink)' }}>{m.reservas}</b></span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 8, borderTop: '1px solid var(--line-2)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>RESERVACIONES</div>
              <div style={fila}><span>No llegaron (no-show)</span><b>{m.noShow}</b></div>
              <div style={fila}><span>Canceladas</span><b>{m.reservasCanceladas}</b></div>
            </div>
          </Tarjeta>
        </div>

        {datos.actual.walkins && <Walkins w={datos.actual.walkins} />}

        <Tarjeta titulo="Desempeño por mesero">
          <Tabla columnas={[{ t: 'MESERO', w: 'minmax(0,1fr)' }, { t: 'TICKETS', w: '80px', der: true }, { t: 'VENTAS', w: '110px', der: true }, { t: 'PROPINA PROM.', w: '120px', der: true }]}
            filas={(datos.actual.meseros || []).map((x: any) => [x.mesero, x.cuentas, <b>{money(num(x.total))}</b>, <span style={{ color: 'var(--muted)' }}>{num(x.total) ? (num(x.propina) / num(x.total) * 100).toFixed(1) : '0.0'}%</span>])} />
        </Tarjeta>

        <Tarjeta titulo="Tickets cancelados recientes">
          {!m.cancelados.recientes.length && <div style={{ color: 'var(--muted)' }}>Ninguno en este periodo.</div>}
          {m.cancelados.recientes.map((c: any) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 4px', borderBottom: '1px solid var(--line-2)' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{c.mesa != null ? `Mesa ${c.mesa}` : `Para llevar${c.cliente_nombre ? ` · ${c.cliente_nombre}` : ''}`}</span>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>{new Date(c.fecha).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {c.mesero}</span>
              </div>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)' }}>{money(c.monto)}</span>
            </div>
          ))}
        </Tarjeta>

        <ReporteHoras fechaInicio={p.actual[0]} fechaFin={p.actual[1]} />
        <ReporteMesas fechaInicio={p.actual[0]} fechaFin={p.actual[1]} />
      </>}
    </div>
  );
}

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Cuándo llegan los clientes sin reservación: por hora y por día de la semana
// (promedio por día abierto, para no premiar al día que se repitió más veces).
function Walkins({ w }: { w: any }) {
  const porDia = new Map((w.por_dia || []).map((d: any) => [d.dow, d]));
  const dias = [1, 2, 3, 4, 5, 6, 0].map(dow => {
    const d: any = porDia.get(dow);
    return { label: DIAS[dow], valor: d ? d.cuentas / (d.dias || 1) : 0 };
  });
  const horas = (w.por_hora || []).map((h: any) => ({ label: `${h.hora}h`, valor: h.cuentas / (h.dias || 1) }));
  const pico = [...horas].sort((a, b) => b.valor - a.valor)[0];
  const mejorDia = [...dias].sort((a, b) => b.valor - a.valor)[0];
  const f1 = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 1 });
  const dif = w.ticket && w.ticket_reserva ? Math.round((w.ticket / w.ticket_reserva - 1) * 100) : null;

  return (
    <Tarjeta titulo="Cuándo llegan sin reservación" gap={16}
      extra={<span style={{ fontSize: 13, color: 'var(--muted)' }}>mesas por día abierto</span>}>
      {!w.cuentas ? <div style={{ color: 'var(--muted)' }}>Sin mesas sin reservación en este periodo.</div> : <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 28px', fontSize: 15 }}>
          {pico && <span>Hora más fuerte <b>{pico.label}</b> <span style={{ color: 'var(--muted)' }}>({f1(pico.valor)} mesas/día)</span></span>}
          {mejorDia?.valor > 0 && <span>Día más fuerte <b>{mejorDia.label}</b> <span style={{ color: 'var(--muted)' }}>({f1(mejorDia.valor)} mesas)</span></span>}
          {w.ticket != null && <span>Gastan en promedio <b>{money(w.ticket)}</b>{dif !== null && <span style={{ color: 'var(--muted)' }}> ({dif >= 0 ? '+' : ''}{dif}% vs. los que reservan)</span>}</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>POR HORA</div>
            <Barras datos={horas} alto={130} formato={f1} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>POR DÍA DE LA SEMANA</div>
            <Barras datos={dias} alto={130} formato={f1} />
          </div>
        </div>
      </>}
    </Tarjeta>
  );
}
